import {HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable, OnDestroy, Optional} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Event, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {EnvironmentService} from '@aztrix/environment';
import {Subscription} from 'rxjs';
import {first} from 'rxjs/operators';

import {MATOMO_SITE_ID} from '../../matomo-site-id';

declare let window: {
  [key: string]: any;
  prototype: Window;
  new (): Window;
};

export interface MatomoConfig {
  trackerUrl?: string;
  siteId?: string;
  cookieDomain?: string;
}

export enum RegisterStepsCompletedType {
  CLAIM = 'CLAIM',
  AUTHENTICATION = 'AUTHENTICATION',
  USER_DATA = 'USER_DATA',
  ACCOUNT_TYPE = 'ACCOUNT_TYPE',
  PROFILE_DATA = 'PROFILE_DATA',
  VERIFICATION = 'VERIFICATION',
  SUCCESS = 'SUCCESS',
}

export enum UpgradeTierActionType {
  TIER_UPGRADE = 'TIER_UPGRADE',
  TIER_UPGRADE_CHECKOUT_BILLING_INFO = 'TIER_UPGRADE_CHECKOUT_BILLING_INFO',
  TIER_UPGRADE_CHECKOUT_PAYMENT = 'TIER_UPGRADE_CHECKOUT_PAYMENT',
  TIER_UPGRADE_CHECKOUT_COMPLETE = 'TIER_UPGRADE_CHECKOUT_COMPLETE',
}

@Injectable({providedIn: 'root'})
export class MatomoService implements OnDestroy {
  private _subscription: Subscription;
  private _customDimension1?: string;
  private _customDimension2?: string;

  constructor(
    _router: Router,
    private _environment: EnvironmentService,
    private _title: Title,
    @Optional() @Inject(MATOMO_SITE_ID) private injectedSiteId: string
  ) {
    window._paq = window._paq || [];

    if (this.injectedSiteId) {
      this.initializeMatomo(this.injectedSiteId);
    } else {
      this._environment
        .string$('MATOMO_SITE_ID')
        .pipe(first((id) => !!id))
        .subscribe((siteId) => {
          this.initializeMatomo(siteId || '');
        });
    }

    this._subscription = _router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this._customDimension1 = undefined;
        this._customDimension2 = undefined;
      }

      if (event instanceof NavigationEnd) {
        window._paq.push(['setCustomUrl', event.url]);
        window._paq.push(['setDocumentTitle', this._title.getTitle()]);

        const dimensions: any = {};
        if (this._customDimension1) dimensions.dimension1 = this._customDimension1;
        if (this._customDimension2) dimensions.dimension2 = this._customDimension2;
        window._paq.push(['trackPageView']);
      }
    });
  }

  private initializeMatomo(siteId: string) {
    window._paq.push(['setSiteId', siteId]);

    this._environment
      .string$('MATOMO_TRACKER_URL')
      .pipe(first((url) => !!url))
      .subscribe((trackerUrl) => {
        const matomoUrl = `https://${trackerUrl}/`;
        window._paq.push(['setTrackerUrl', matomoUrl + 'matomo.php']);

        this.injectScript(matomoUrl + 'matomo.js');
        window._paq.push(['trackPageView']);
        window._paq.push(['enableLinkTracking']);
      });

    this._environment
      .string$('MATOMO_COOKIE_DOMAIN')
      .pipe(first((domain) => !!domain))
      .subscribe((cookieDomain) => {
        window._paq.push(['setCookieDomain', cookieDomain]);
      });

    if (this.consentGiven) {
      window._paq.push(['setCookieConsentGiven']);
    }
  }

  private injectScript(scriptUrl: string) {
    if (document.getElementById('matomo-script')) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = 'matomo-script';
    script.src = scriptUrl;
    document.body.insertBefore(script, document.body.firstChild);
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this.setAlistDimension(undefined);
    this.setAlistLanguageDimension(undefined);
  }

  setConsentGiven() {
    this._setConsentGivenLocalStorage(true);
    window._paq.push(['setCookieConsentGiven']);
  }

  removeConsentGiven() {
    this._setConsentGivenLocalStorage(false);
    window._paq.push(['forgetCookieConsentGiven']);
  }

  trackEvent(category: string, action: string, name?: string, value?: string | number) {
    const params = ['trackEvent', category, action];
    if (name) params.push(name);
    if (value) params.push(value.toString());

    window._paq.push(params);
  }

  setAlistDimension(alistId: string | undefined) {
    this._customDimension1 = alistId;
    if (alistId) {
      window._paq.push(['setCustomDimension', 1, alistId]);
    } else {
      window._paq.push(['deleteCustomDimension', 1]);
    }
  }

  setAlistLanguageDimension(alistLanguageId: string | undefined) {
    this._customDimension2 = alistLanguageId;
    if (alistLanguageId) {
      window._paq.push(['setCustomDimension', 2, alistLanguageId]);
    } else {
      window._paq.push(['deleteCustomDimension', 2]);
    }
  }

  trackError(proposalId: string, agreementId: string, error: HttpErrorResponse) {
    const errorData = {
      proposalId,
      agreementId,
      error: error.message || error.statusText,
    };
    this.trackEvent('subscribe', 'error', JSON.stringify(errorData));
  }

  get consentAsked(): boolean {
    return localStorage.getItem('matomoConsentGiven') !== null;
  }
  get consentGiven(): boolean {
    return this.consentAsked && localStorage.getItem('matomoConsentGiven') === 'true';
  }

  private _setConsentGivenLocalStorage(value: boolean) {
    localStorage.setItem('matomoConsentGiven', value ? 'true' : 'false');
  }
}
